// On-screen button control surface for STARCODE.
//
// Why this exists: several of the original NEU.pde keyboard shortcuts (the number row, "ß",
// held A/S, Cmd/Ctrl+Shift combos, ...) collide with browser/OS-reserved shortcuts and layout-
// dependent key codes, so they don't reliably reach the page in every browser/keyboard layout.
// Small on-screen buttons sidestep all of that entirely - a click is a click, no interference.
//
// Keyboard shortcuts in keybinds.js are left in place as a bonus for anyone who wants them; this
// module is the primary, interference-free way to drive every single effect.

import { state, toggleEffect, resetEffects, setPermanentColor } from "./state.js";
import { MIDI_TARGETS } from "./midi.js";
import { PARTICLE_COUNT, PARTICLE_PRESETS } from "./ParticleSystem.js";
import { t, getLang } from "./i18n.js";

export function exportPresetJSON() {
  const exportData = {
    app: "STARCODE",
    version: "1.0",
    date: new Date().toISOString(),
    state: {}
  };

  const skipKeys = new Set([
    "midiConnected", "audioReady", "lastToggledEffect", "loadedObjName",
    "explodeTime", "shockwaveTime", "shockwaveStrength", "midiValue"
  ]);

  for (const key in state) {
    if (typeof state[key] !== "function" && !skipKeys.has(key)) {
      exportData.state[key] = state[key];
    }
  }

  const jsonStr = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonStr], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const nowStr = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  a.download = `starcode-preset-${nowStr}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function importPresetJSON(jsonText) {
  try {
    const data = JSON.parse(jsonText);
    const stateObj = data.state || data;
    if (!stateObj || typeof stateObj !== "object") {
      alert("Ungültiges Preset-Format.");
      return false;
    }

    for (const key in stateObj) {
      if (key in state && typeof state[key] !== "function") {
        state[key] = stateObj[key];
      }
    }

    if (stateObj.outputWidth || stateObj.outputHeight || stateObj.outputPreset) {
      window.dispatchEvent(new Event("resize"));
    }

    return true;
  } catch (err) {
    alert("Fehler beim Laden des Presets: " + err.message);
    return false;
  }
}

function getCategories() {
  return [
    {
      catId: "catAttract",
      title: t("catPhysics"),
      sections: [
        {
          title: t("catPhysicsSec1"),
          items: [
            { kind: "toggle", label: t("btnAudioReact"), name: "audioReactAttractors", field: "audioReactAttractors", classExtra: "reactToggle" },
            { kind: "held", label: t("btnAttractorNeg"), field: "negAttractorHeld" },
            { kind: "held", label: t("btnAttractorPos"), field: "posAttractorHeld" },
          ],
        },
        {
          title: t("catPhysicsSec2"),
          items: [
            { kind: "toggle", label: t("btnAudioReact"), name: "audioReactBasis", field: "audioReactBasis", classExtra: "reactToggle" },
            { kind: "toggle", label: t("btnGravity"), name: "gravity", field: "gravityOn" },
            { kind: "toggle", label: t("btnGravityWells"), name: "wellsOn", field: "wellsOn" },
            { kind: "toggle", label: t("btnEmber"), name: "emberOn", field: "emberOn" },
            { kind: "toggle", label: t("btnNebula"), name: "nebulaOn", field: "nebulaOn" },
            { kind: "toggle", label: t("btnChaosBoost"), name: "chaosBoostOn", field: "chaosBoostOn" },
            { kind: "toggle", label: t("btnSlowMotion"), name: "slowMotion", field: "slowMotion" },
            { kind: "oneshot", label: t("btnShockwave"), action: "shockwave", classExtra: "shockwaveBtn" },
            { kind: "oneshot", label: t("btnExplosion"), action: "explosion" },
            { kind: "oneshot", label: t("btnReset"), action: "reset" },
          ],
        },
      ],
    },
    {
      catId: "catShapes",
      title: t("catFormz"),
      sections: [
        {
          title: t("catFormzSec1"),
          items: [
            { kind: "toggle", label: t("btnAudioReact"), name: "audioReactEffects", field: "audioReactEffects", classExtra: "reactToggle" },
            { kind: "toggle", label: t("btnBlackHole"), name: "blackHoleOn", field: "blackHoleOn", classExtra: "blackHoleBtn" },
            { kind: "toggle", label: t("btnNeutronStar"), name: "neutronStarOn", field: "neutronStarOn", classExtra: "neutronStarBtn" },
            { kind: "toggle", label: t("btnSun"), name: "sunOn", field: "sunOn", classExtra: "sunBtn" },
            { kind: "toggle", label: t("btnGalaxySpiral"), name: "galaxySpiralOn", field: "galaxySpiralOn" },
            { kind: "toggle", label: t("btnGalaxyDisc"), name: "galaxyDiscOn", field: "galaxyDiscOn" },
            { kind: "toggle", label: t("btnSphere"), name: "sphereOn", field: "sphereOn", classExtra: "sphereBtn" },
            { kind: "toggle", label: t("btnAudioRing"), name: "ring", field: "ringOn" },
          ],
        },
        {
          title: t("catFormzSec2"),
          items: [
            { kind: "toggle", label: t("btnCyberGrid"), name: "cyberGridOn", field: "cyberGridOn", classExtra: "cyberGridBtn" },
            { kind: "toggle", label: t("btnVortex"), name: "vortexOn", field: "vortexOn" },
            { kind: "toggle", label: t("btnPortal"), name: "portalOn", field: "portalOn" },
            { kind: "toggle", label: t("btnTunnel"), name: "tunnelOn", field: "tunnelOn" },
            { kind: "toggle", label: t("btnHelix"), name: "helixOn", field: "helixOn" },
            { kind: "toggle", label: t("btnFractal"), name: "fractalOn", field: "fractalOn" },
            { kind: "toggle", label: t("btnGridPulse"), name: "gridPulseOn", field: "gridPulseOn" },
            { kind: "toggle", label: t("btnPattern"), name: "patternOn", field: "patternOn" },
            { kind: "toggle", label: t("btnDistortion"), name: "distortionOn", field: "distortionOn" },
            { kind: "toggle", label: t("btnSwarm"), name: "swarmOn", field: "swarmOn" },
            { kind: "toggle", label: t("btnStreams"), name: "streamsOn", field: "streamsOn" },
          ],
        },
      ],
    },
    {
      catId: "catAudio",
      title: t("catAudio"),
      sections: [
        {
          title: t("catAudioSec1"),
          items: [
            { kind: "toggle", label: t("btnBioluminescence"), name: "bioluminescenceOn", field: "bioluminescenceOn" },
            { kind: "toggle", label: t("btnSpectrumColor"), name: "spectrumColorOn", field: "spectrumColorOn" },
            { kind: "toggle", label: t("btnChromaWave"), name: "chromaWaveOn", field: "chromaWaveOn" },
            { kind: "toggle", label: t("btnBeatFlash"), name: "beatFlashOn", field: "beatFlashOn" },
            { kind: "toggle", label: t("btnShimmer"), name: "shimmerOn", field: "shimmerOn" },
            { kind: "toggle", label: t("btnStrobeField"), name: "strobeFieldOn", field: "strobeFieldOn" },
            { kind: "toggle", label: t("btnStarSky"), name: "starSkyOn", field: "starSkyOn" },
            { kind: "toggle", label: t("btnColorInvert"), name: "colorInvertOn", field: "colorInvertOn" },
            { kind: "toggle", label: t("btnColorMid"), name: "colorMidOn", field: "colorMidOn" },
          ],
        },
        {
          title: t("catAudioSec2"),
          items: [
            { kind: "toggle", label: t("btnBeatPulse"), name: "beatPulseOn", field: "beatPulseOn" },
            { kind: "toggle", label: t("btnScaleFreq"), name: "scalePulseOn", field: "scalePulseOn" },
            { kind: "toggle", label: t("btnAudioSize"), name: "audioSizeOn", field: "audioSizeOn" },
            { kind: "toggle", label: t("btnBreath"), name: "breathOn", field: "breathOn" },
            { kind: "toggle", label: t("btnShapeChange"), name: "shapeChangeOn", field: "shapeChangeOn" },
            { kind: "toggle", label: t("btnBandExplode"), name: "bandExplodeOn", field: "bandExplodeOn" },
            { kind: "toggle", label: t("btnComplex"), name: "complexOn", field: "complexOn" },
            { kind: "toggle", label: t("btnLiquidRipple"), name: "liquidRippleOn", field: "liquidRippleOn" },
            { kind: "toggle", label: t("btnRipple"), name: "ripple", field: "rippleOn" },
            { kind: "toggle", label: t("btnCaustics"), name: "causticsOn", field: "causticsOn" },
            { kind: "toggle", label: t("btnInkBleed"), name: "inkBleedOn", field: "inkBleedOn" },
            { kind: "oneshot", label: t("btnSizeCycle"), action: "sizeCycle" },
          ],
        },
      ],
    },
    {
      catId: "catLive",
      title: t("catLive"),
      sections: [
        {
          title: t("catLiveSec1"),
          items: [
            { kind: "openOutput", label: t("btnOutputWindow") },
            { kind: "pip", label: t("btnPip"), classExtra: "pipBtn" },
            { kind: "fullscreen", label: t("btnFullscreen") },
            { kind: "toggle", label: t("btnReactiveMaster"), name: "audioReactGlobal", field: "audioReactGlobal", classExtra: "reactMaster" },
            { kind: "toggle", label: t("btnSpin360"), name: "spin360On", field: "spin360On", classExtra: "spin360Btn" },
            { kind: "toggle", label: t("btnAutoMix"), name: "autoMixOn", field: "autoMixOn" },
            { kind: "toggle", label: t("btnWrapMode"), name: "wrapModeOn", field: "wrapModeOn" },
            { kind: "liveMode", label: t("btnLiveMode") },
          ],
        },
        {
          title: t("catLiveSec2"),
          items: [
            { kind: "morph", label: t("btnStarcodeMorph") },
            { kind: "brandMorph", label: t("btnBrandMorph"), classExtra: "brandBtn" },
            { kind: "objMorph", label: t("btnObjMorph") },
            { kind: "loadObj", label: t("btnLoadObj"), classExtra: "loadObjBtn" },
            { kind: "permanentColor", label: t("btnBluePerm"), mode: 1 },
            { kind: "permanentColor", label: t("btnRedPerm"), mode: 2 },
            { kind: "permanentColor", label: t("btnBwPerm"), mode: 3, classExtra: "bwBtn" },
          ],
        },
      ],
    },
    {
      catId: "catEssentials",
      title: t("catEssentials"),
      sections: [
        {
          title: t("catEssentialsSec1"),
          items: [
            { kind: "resetCamera", label: t("btnFindSwarm"), classExtra: "findSwarmBtn" },
            { kind: "toggle", label: t("btnCameraFollow"), name: "cameraFollowOn", field: "cameraFollowOn", classExtra: "followBtn" },
            { kind: "exportPreset", label: t("btnExportPreset"), classExtra: "exportPresetBtn" },
            { kind: "importPreset", label: t("btnImportPreset"), classExtra: "importPresetBtn" },
          ],
        },
      ],
    },
  ];
}

// Global performance parameters exposed as sliders. Every one of these is also MIDI-assignable
// (see midi.js) - the sliders are the mouse equivalent for when no controller is plugged in.
function getSliders() {
  return [
    { key: "master", field: "masterIntensity", label: t("sliderMaster"), min: 0, max: 2, step: 0.01 },
    { key: "size", field: "sizeBoost", label: t("sliderSize"), min: 0.25, max: 3, step: 0.01 },
    { key: "speed", field: "speedScale", label: t("sliderSpeed"), min: 0.05, max: 2, step: 0.01 },
    { key: "rotation", field: "rotationSpeed", label: t("sliderRotation"), min: 0, max: 3, step: 0.02 },
    { key: "bloom", field: "bloomAmount", label: t("sliderBloom"), min: 0, max: 3, step: 0.01 },
    { key: "gain", field: "audioGain", label: t("sliderAudioGain"), min: 0, max: 3, step: 0.01 },
    { key: "bioIntensity", field: "bioIntensity", label: t("sliderBioIntensity"), min: 0, max: 2, step: 0.01 },
    { key: "focus", field: "focusDistance", label: t("sliderFocusDistance"), min: 200, max: 1600, step: 10 },
    { key: "dofBlur", field: "dofBlur", label: t("sliderDofBlur"), min: 0, max: 2, step: 0.01 },
    { key: "autoMixIntensity", field: "autoMixIntensity", label: t("sliderAutoMixTempo"), min: 0, max: 1, step: 0.01 },
  ];
}

export function installControlBar(options) {
  const {
    particleSystem,
    onMorphToggle,
    onLiveModeToggle,
    onFullscreenToggle,
    onResetCamera,
    onOpenOutput,
    onPipToggle,
    onOneshot,
    onObjLoaded,
    midi,
  } = options;

  const container = document.getElementById("controlBar");
  if (!container) return () => {};

  let trackedButtons = [];
  let trackedSliders = [];
  let trackedLearnButtons = [];
  let trackedCategories = [];
  let inputW = null;
  let inputH = null;
  let formatSelect = null;
  let thumb = null;
  let colorStatus = null;
  let colorResetBtn = null;

  function renderBar() {
    container.innerHTML = "";
    trackedButtons = [];
    trackedSliders = [];
    trackedLearnButtons = [];
    trackedCategories = [];

    const CATEGORIES = getCategories();
    const SLIDERS = getSliders();

    function makeCollapsible(box, headerEl, storageKey, defaultCollapsed = false) {
      headerEl.classList.add("collapsibleHeader");
      headerEl.title = t("tipCollapse");
      const stored = localStorage.getItem(storageKey);
      let collapsed = stored === null ? defaultCollapsed : stored === "1";
      const apply = () => {
        box.classList.toggle("collapsed", collapsed);
        headerEl.dataset.chevron = collapsed ? "▸" : "▾";
      };
      apply();
      headerEl.addEventListener("click", () => {
        collapsed = !collapsed;
        localStorage.setItem(storageKey, collapsed ? "1" : "0");
        apply();
      });
    }

    // --- Category Grid: side-by-side grouped boxes ---
    const grid = document.createElement("div");
    grid.className = "categoryGrid";

    for (const cat of CATEGORIES) {
      const card = document.createElement("div");
      card.className = "categoryCard";
      card.id = cat.catId;

      const header = document.createElement("div");
      header.className = "categoryHeader";
      header.textContent = cat.title;
      const badge = document.createElement("span");
      badge.className = "categoryBadge";
      header.appendChild(badge);
      card.appendChild(header);

      const body = document.createElement("div");
      body.className = "categoryBody";
      trackedCategories.push({ card, badge, from: trackedButtons.length });

      for (const section of cat.sections) {
        const row = document.createElement("div");
        row.className = "controlSection";

        const label = document.createElement("span");
        label.className = "controlSectionLabel";
        label.textContent = section.title;
        row.appendChild(label);

        for (const item of section.items) {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "ctrlBtn";
          if (item.classExtra) btn.classList.add(item.classExtra);
          btn.textContent = item.label;

          if (item.kind === "toggle") {
            btn.addEventListener("click", () => toggleEffect(item.name));
            trackedButtons.push({ el: btn, kind: "toggle", field: item.field });
          } else if (item.kind === "held") {
            const press = (e) => {
              e.preventDefault();
              state[item.field] = true;
            };
            const release = () => {
              state[item.field] = false;
            };
            btn.addEventListener("pointerdown", press);
            btn.addEventListener("pointerup", release);
            btn.addEventListener("pointerleave", release);
            btn.addEventListener("pointercancel", release);
            trackedButtons.push({ el: btn, kind: "held", field: item.field });
          } else if (item.kind === "liveMode") {
            btn.addEventListener("click", () => onLiveModeToggle?.());
            trackedButtons.push({ el: btn, kind: "toggle", field: "liveModeOn" });
          } else if (item.kind === "openOutput") {
            btn.addEventListener("click", () => onOpenOutput?.());
          } else if (item.kind === "pip") {
            btn.addEventListener("click", () => onPipToggle?.());
          } else if (item.kind === "fullscreen") {
            btn.addEventListener("click", () => onFullscreenToggle?.());
          } else if (item.kind === "resetCamera") {
            btn.addEventListener("click", () => onResetCamera?.());
          } else if (item.kind === "exportPreset") {
            btn.addEventListener("click", () => exportPresetJSON());
          } else if (item.kind === "importPreset") {
            const fileInput = document.createElement("input");
            fileInput.type = "file";
            fileInput.accept = ".json";
            fileInput.style.display = "none";
            fileInput.addEventListener("change", async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const text = await file.text();
                const success = importPresetJSON(text);
                if (success) {
                  btn.textContent = `✓ ${file.name.slice(0, 10)}`;
                  setTimeout(() => {
                    btn.textContent = item.label;
                  }, 2000);
                }
              } catch (err) {
                alert("Fehler beim Lesen der Datei: " + err.message);
              }
            });
            btn.appendChild(fileInput);
            btn.addEventListener("click", () => fileInput.click());
          } else if (item.kind === "oneshot") {
            btn.addEventListener("click", () => {
              switch (item.action) {
                case "sizeCycle":
                  state.sizeCyclePhase = (state.sizeCyclePhase + 1) % 4;
                  break;
                case "explosion":
                  onOneshot ? onOneshot("explosion") : particleSystem.triggerExplosion(90);
                  break;
                case "rbBurst":
                  onOneshot ? onOneshot("rbBurst") : particleSystem.triggerRbBurst(90);
                  break;
                case "shockwave":
                  onOneshot ? onOneshot("shockwave") : particleSystem.triggerShockwave(180);
                  break;
                case "reset":
                  resetEffects();
                  onOneshot ? onOneshot("resetSwarm") : particleSystem.resetSwarm();
                  break;
                default:
                  break;
              }
            });
          } else if (item.kind === "morph") {
            btn.addEventListener("click", () => {
              toggleEffect("morph");
              onMorphToggle?.(state.morphOn);
            });
            trackedButtons.push({ el: btn, kind: "morph" });
          } else if (item.kind === "objMorph") {
            btn.addEventListener("click", () => {
              toggleEffect("objMorph");
            });
            trackedButtons.push({ el: btn, kind: "objMorph" });
          } else if (item.kind === "brandMorph") {
            btn.addEventListener("click", () => {
              toggleEffect("brandMorph");
            });
            trackedButtons.push({ el: btn, kind: "brandMorph" });
          } else if (item.kind === "loadObj") {
            const fileInput = document.createElement("input");
            fileInput.type = "file";
            fileInput.accept = ".obj";
            fileInput.style.display = "none";
            fileInput.addEventListener("change", async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const text = await file.text();
                const res = onObjLoaded ? onObjLoaded(text, file.name) : particleSystem.loadCustomObj(text, file.name);
                if (res && res.success !== false) {
                  btn.textContent = `✓ ${file.name.slice(0, 9)}`;
                  state.objMorphOn = true;
                  state.objMorphToggleTime = performance.now() / 1000;
                } else {
                  alert("Fehler beim Laden des OBJ: " + (res?.error || "Ungültiges Format"));
                }
              } catch (err) {
                alert("Fehler beim Lesen der Datei: " + err.message);
              }
            });
            btn.appendChild(fileInput);
            btn.addEventListener("click", () => fileInput.click());
          } else if (item.kind === "permanentColor") {
            btn.addEventListener("click", () => setPermanentColor(item.mode));
            trackedButtons.push({ el: btn, kind: "permanentColor", mode: item.mode });
          }

          row.appendChild(btn);
        }

        body.appendChild(row);
      }

      card.appendChild(body);
      grid.appendChild(card);
      trackedCategories[trackedCategories.length - 1].to = trackedButtons.length;
    }

    container.appendChild(grid);

    // --- Bottom Tools Bar ---
    const bottomBar = document.createElement("div");
    bottomBar.className = "bottomToolsBar";

    // Sliders Box
    const sliderBox = document.createElement("div");
    sliderBox.className = "toolBox sliderBox";
    const sliderHeader = document.createElement("div");
    sliderHeader.className = "toolBoxHeader";
    sliderHeader.textContent = t("boxControls");
    sliderBox.appendChild(sliderHeader);
    makeCollapsible(sliderBox, sliderHeader, "starcode.ui.sliderBox");

    const sliderRow = document.createElement("div");
    sliderRow.className = "sliderRow";

    for (const def of SLIDERS) {
      const wrap = document.createElement("label");
      wrap.className = "ctrlSlider";

      const name = document.createElement("span");
      name.textContent = def.label;

      const input = document.createElement("input");
      input.type = "range";
      input.min = def.min;
      input.max = def.max;
      input.step = def.step;
      input.value = state[def.field];
      input.addEventListener("input", () => {
        state[def.field] = parseFloat(input.value);
      });

      wrap.appendChild(name);
      wrap.appendChild(input);

      if (def.key === "rotation") {
        const rotAudioLabel = document.createElement("label");
        rotAudioLabel.className = "sliderCheckboxWrap";
        rotAudioLabel.title = t("tipRotAudio");
        const rotAudioCheck = document.createElement("input");
        rotAudioCheck.type = "checkbox";
        rotAudioCheck.className = "sliderCheckbox";
        rotAudioCheck.checked = !!state.rotationAudioReact;
        rotAudioCheck.addEventListener("change", () => {
          state.rotationAudioReact = rotAudioCheck.checked;
        });
        const rotAudioText = document.createElement("span");
        rotAudioText.className = "sliderCheckboxText";
        rotAudioText.textContent = t("btnAudioRot");
        rotAudioLabel.appendChild(rotAudioCheck);
        rotAudioLabel.appendChild(rotAudioText);
        wrap.appendChild(rotAudioLabel);
        trackedButtons.push({ el: rotAudioCheck, kind: "checkbox", field: "rotationAudioReact" });
      }

      if (def.key === "focus") {
        const afBtn = document.createElement("button");
        afBtn.type = "button";
        afBtn.className = "ctrlBtn autoFocusBtn";
        afBtn.title = t("tipAutofocus");
        afBtn.textContent = state.autofocusOn ? t("btnAFActive") : t("btnAF");
        afBtn.addEventListener("click", (e) => {
          e.preventDefault();
          state.autofocusOn = !state.autofocusOn;
          afBtn.textContent = state.autofocusOn ? t("btnAFActive") : t("btnAF");
          afBtn.classList.toggle("active", state.autofocusOn);
        });
        wrap.appendChild(afBtn);
        trackedButtons.push({ el: afBtn, kind: "toggle", field: "autofocusOn" });
      }

      if (def.key === "dofBlur") {
        const invertBtn = document.createElement("button");
        invertBtn.type = "button";
        invertBtn.className = "ctrlBtn dofInvertBtn";
        invertBtn.title = t("tipDofInvert");
        invertBtn.textContent = state.dofInvert ? t("btnNearBokeh") : t("btnFarBokeh");
        invertBtn.addEventListener("click", (e) => {
          e.preventDefault();
          state.dofInvert = !state.dofInvert;
          invertBtn.textContent = state.dofInvert ? t("btnNearBokeh") : t("btnFarBokeh");
          invertBtn.classList.toggle("active", state.dofInvert);
        });
        wrap.appendChild(invertBtn);
        trackedButtons.push({ el: invertBtn, kind: "toggle", field: "dofInvert" });

        const bokehAudioBtn = document.createElement("button");
        bokehAudioBtn.type = "button";
        bokehAudioBtn.className = "ctrlBtn bokehAudioBtn";
        bokehAudioBtn.title = t("tipBokehAudio");
        bokehAudioBtn.textContent = state.dofAudioReact ? t("btnAudioBokehActive") : t("btnAudioBokeh");
        bokehAudioBtn.addEventListener("click", (e) => {
          e.preventDefault();
          state.dofAudioReact = !state.dofAudioReact;
          bokehAudioBtn.textContent = state.dofAudioReact ? t("btnAudioBokehActive") : t("btnAudioBokeh");
          bokehAudioBtn.classList.toggle("active", state.dofAudioReact);
        });
        wrap.appendChild(bokehAudioBtn);
        trackedButtons.push({ el: bokehAudioBtn, kind: "toggle", field: "dofAudioReact" });
      }

      sliderRow.appendChild(wrap);
      trackedSliders.push({ input, field: def.field });
    }

    // Inline Color Spectrum Slider
    const colorWrap = document.createElement("label");
    colorWrap.className = "ctrlSlider ctrlSliderColor";

    const colorName = document.createElement("span");
    colorName.textContent = t("sliderHue");
    colorWrap.appendChild(colorName);

    const barWrap = document.createElement("div");
    barWrap.className = "colorBarWrap";
    barWrap.title = t("tipColorScrub");

    const barTrack = document.createElement("div");
    barTrack.className = "colorBarTrack";

    thumb = document.createElement("div");
    thumb.className = "colorBarThumb";

    barWrap.appendChild(barTrack);
    barWrap.appendChild(thumb);
    colorWrap.appendChild(barWrap);

    colorStatus = document.createElement("span");
    colorStatus.className = "colorBarStatus";
    colorStatus.textContent = state.colorWheelActive ? `${Math.round(state.hueShift * 360)}°` : t("colorOff");
    colorWrap.appendChild(colorStatus);

    colorResetBtn = document.createElement("button");
    colorResetBtn.type = "button";
    colorResetBtn.className = "ctrlBtn colorResetBtn";
    colorResetBtn.textContent = "✕";
    colorResetBtn.title = t("tipColorReset");
    colorResetBtn.addEventListener("click", (e) => {
      e.preventDefault();
      state.colorWheelActive = false;
      state.hueShift = 0.0;
    });
    colorWrap.appendChild(colorResetBtn);

    function updateHueFromPointer(e) {
      const rect = barTrack.getBoundingClientRect();
      if (rect.width <= 0) return;
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      const ratio = x / rect.width;
      state.hueShift = ratio;
      state.colorWheelActive = true;
    }

    let isDraggingColor = false;
    barWrap.addEventListener("pointerdown", (e) => {
      isDraggingColor = true;
      try { barWrap.setPointerCapture(e.pointerId); } catch (_) {}
      updateHueFromPointer(e);
    });
    barWrap.addEventListener("pointermove", (e) => {
      if (isDraggingColor) updateHueFromPointer(e);
    });
    const endDrag = (e) => {
      if (isDraggingColor) {
        isDraggingColor = false;
        try { barWrap.releasePointerCapture(e.pointerId); } catch (_) {}
      }
    };
    barWrap.addEventListener("pointerup", endDrag);
    barWrap.addEventListener("pointercancel", endDrag);

    sliderRow.appendChild(colorWrap);
    sliderBox.appendChild(sliderRow);
    bottomBar.appendChild(sliderBox);

    // Format Box
    const formatBox = document.createElement("div");
    formatBox.className = "toolBox formatBox";

    const formatHeader = document.createElement("div");
    formatHeader.className = "toolBoxHeader";
    formatHeader.textContent = t("boxFormat");
    formatBox.appendChild(formatHeader);
    makeCollapsible(formatBox, formatHeader, "starcode.ui.formatBox");

    const formatContent = document.createElement("div");
    formatContent.className = "formatContent";

    formatSelect = document.createElement("select");
    formatSelect.className = "formatSelect";
    const isEn = getLang() === "en";
    const PRESETS = [
      { label: isEn ? "16:9 Widescreen (1920 × 1080) — Full HD" : "16:9 Breitbild (1920 × 1080) — Full HD", w: 1920, h: 1080, id: "16:9_1080" },
      { label: isEn ? "16:9 Widescreen (1280 × 720) — HD" : "16:9 Breitbild (1280 × 720) — HD", w: 1280, h: 720, id: "16:9_720" },
      { label: isEn ? "16:9 Widescreen (3840 × 2160) — 4K UHD" : "16:9 Breitbild (3840 × 2160) — 4K UHD", w: 3840, h: 2160, id: "16:9_4k" },
      { label: "16:10 (1920 × 1200)", w: 1920, h: 1200, id: "16:10" },
      { label: "21:9 Ultra-Wide (2560 × 1080)", w: 2560, h: 1080, id: "21:9" },
      { label: isEn ? "4:3 Classic (1440 × 1080)" : "4:3 Klassisch (1440 × 1080)", w: 1440, h: 1080, id: "4:3" },
      { label: isEn ? "1:1 Square (1080 × 1080)" : "1:1 Quadrat (1080 × 1080)", w: 1080, h: 1080, id: "1:1" },
      { label: isEn ? "9:16 Vertical / Social (1080 × 1920)" : "9:16 Vertikal / Social (1080 × 1920)", w: 1080, h: 1920, id: "9:16" },
      { label: isEn ? "Custom…" : "Benutzerdefiniert…", w: state.outputWidth || 1920, h: state.outputHeight || 1080, id: "custom" },
    ];

    for (const p of PRESETS) {
      const opt = document.createElement("option");
      opt.value = p.id;
      opt.textContent = p.label;
      if (p.id === (state.outputPreset || "16:9_1080")) opt.selected = true;
      formatSelect.appendChild(opt);
    }

    const formatInputs = document.createElement("div");
    formatInputs.className = "formatInputs";

    const inputWWrap = document.createElement("label");
    inputWWrap.className = "formatInputWrap";
    inputWWrap.innerHTML = `<span class="formatInputLabel">${t("optWidth")}</span>`;
    inputW = document.createElement("input");
    inputW.type = "number";
    inputW.className = "formatNumInput";
    inputW.min = "320";
    inputW.max = "7680";
    inputW.step = "10";
    inputW.value = state.outputWidth || 1920;
    inputWWrap.appendChild(inputW);
    inputWWrap.appendChild(document.createTextNode("px"));

    const inputHWrap = document.createElement("label");
    inputHWrap.className = "formatInputWrap";
    inputHWrap.innerHTML = `<span class="formatInputLabel">${t("optHeight")}</span>`;
    inputH = document.createElement("input");
    inputH.type = "number";
    inputH.className = "formatNumInput";
    inputH.min = "240";
    inputH.max = "4320";
    inputH.step = "10";
    inputH.value = state.outputHeight || 1080;
    inputHWrap.appendChild(inputH);
    inputHWrap.appendChild(document.createTextNode("px"));

    formatSelect.addEventListener("change", () => {
      const chosen = PRESETS.find((p) => p.id === formatSelect.value);
      if (chosen && chosen.id !== "custom") {
        state.outputWidth = chosen.w;
        state.outputHeight = chosen.h;
        state.outputPreset = chosen.id;
        inputW.value = chosen.w;
        inputH.value = chosen.h;
      } else {
        state.outputPreset = "custom";
      }
      window.dispatchEvent(new Event("resize"));
    });

    const onCustomChange = () => {
      state.outputWidth = parseInt(inputW.value, 10) || 1920;
      state.outputHeight = parseInt(inputH.value, 10) || 1080;
      state.outputPreset = "custom";
      formatSelect.value = "custom";
      window.dispatchEvent(new Event("resize"));
    };
    inputW.addEventListener("input", onCustomChange);
    inputH.addEventListener("input", onCustomChange);

    formatInputs.appendChild(inputWWrap);
    formatInputs.appendChild(inputHWrap);

    const particleWrap = document.createElement("label");
    particleWrap.className = "formatInputWrap";
    particleWrap.style.marginTop = "0.2rem";
    particleWrap.innerHTML = `<span class="formatInputLabel">${t("optParticles")}</span>`;
    const particleSelect = document.createElement("select");
    particleSelect.className = "formatSelect";
    particleSelect.style.width = "auto";
    const PARTICLE_OPTIONS = PARTICLE_PRESETS;
    for (const optDef of PARTICLE_OPTIONS) {
      const opt = document.createElement("option");
      opt.value = String(optDef.count);
      opt.textContent = optDef.label;
      if (optDef.count === PARTICLE_COUNT) opt.selected = true;
      particleSelect.appendChild(opt);
    }
    particleSelect.addEventListener("change", () => {
      const chosen = parseInt(particleSelect.value, 10);
      localStorage.setItem("starcode_particle_count", String(chosen));
      particleSystem?.setParticleCount(chosen);
    });
    particleWrap.appendChild(particleSelect);

    formatContent.appendChild(formatSelect);
    formatContent.appendChild(formatInputs);
    formatContent.appendChild(particleWrap);
    formatBox.appendChild(formatContent);
    bottomBar.appendChild(formatBox);

    // Presets Box
    const FACTORY_PRESETS = [
      {
        id: "standard",
        label: "🌟 Standard (Swarm & Flow)",
        state: {
          sunOn: false, fireballOn: false, blackHoleOn: false, neutronStarOn: false, cyberGridOn: false, sphereOn: false,
          bioluminescenceOn: false, spectrumColorOn: false, chromaWaveOn: false, beatFlashOn: false, permanentColor: 0,
          dofBlur: 0.45, dofInvert: false, dofAudioReact: false, rotationSpeed: 0.35, rotationAudioReact: false,
          bloomAmount: 0.45, speedScale: 1.0, masterIntensity: 1.0, hueShift: 0.0, colorWheelActive: false,
        }
      },
      {
        id: "solar_blaze",
        label: "☀️ Solar Blaze (SUN & Korona)",
        state: {
          sunOn: true, fireballOn: false, blackHoleOn: false, neutronStarOn: false, cyberGridOn: false, sphereOn: false,
          bioluminescenceOn: false, spectrumColorOn: false, chromaWaveOn: false, beatFlashOn: false, permanentColor: 0,
          dofBlur: 0.35, dofInvert: false, dofAudioReact: true, rotationSpeed: 0.25, rotationAudioReact: true,
          bloomAmount: 0.55, speedScale: 1.0, masterIntensity: 1.1, hueShift: 0.0, colorWheelActive: false,
        }
      },
      {
        id: "gargantua",
        label: "🌀 Gargantua (Black Hole)",
        state: {
          sunOn: false, fireballOn: false, blackHoleOn: true, neutronStarOn: false, cyberGridOn: false, sphereOn: false,
          bioluminescenceOn: false, spectrumColorOn: false, chromaWaveOn: false, beatFlashOn: false, permanentColor: 0,
          dofBlur: 0.50, dofInvert: false, dofAudioReact: true, rotationSpeed: 0.30, rotationAudioReact: false,
          bloomAmount: 0.60, speedScale: 1.0, masterIntensity: 1.15, hueShift: 0.0, colorWheelActive: false,
        }
      },
      {
        id: "pulsar_jet",
        label: "⚡ Pulsar Matrix (Neutron Star)",
        state: {
          sunOn: false, fireballOn: false, blackHoleOn: false, neutronStarOn: true, cyberGridOn: false, sphereOn: false,
          bioluminescenceOn: false, spectrumColorOn: false, chromaWaveOn: false, beatFlashOn: true, permanentColor: 0,
          dofBlur: 0.45, dofInvert: false, dofAudioReact: true, rotationSpeed: 0.40, rotationAudioReact: true,
          bloomAmount: 0.50, speedScale: 1.1, masterIntensity: 1.2, hueShift: 0.0, colorWheelActive: false,
        }
      },
      {
        id: "cyber_synth",
        label: "👾 Cyber Synth (Z-Grid)",
        state: {
          sunOn: false, fireballOn: false, blackHoleOn: false, neutronStarOn: false, cyberGridOn: true, sphereOn: false,
          bioluminescenceOn: false, spectrumColorOn: false, chromaWaveOn: false, beatFlashOn: false, permanentColor: 0,
          dofBlur: 0.55, dofInvert: true, dofAudioReact: true, rotationSpeed: 0.20, rotationAudioReact: false,
          bloomAmount: 0.50, speedScale: 1.15, masterIntensity: 1.1, hueShift: 0.0, colorWheelActive: false,
        }
      },
      {
        id: "deep_galaxy",
        label: "🌌 Deep Galaxy (Spiral & Nebula)",
        state: {
          sunOn: false, fireballOn: false, blackHoleOn: false, neutronStarOn: false, cyberGridOn: false, sphereOn: false,
          galaxySpiralOn: true, nebulaOn: true, starSkyOn: true, bioluminescenceOn: false, spectrumColorOn: false,
          chromaWaveOn: false, beatFlashOn: false, permanentColor: 0, dofBlur: 0.40, dofInvert: false, dofAudioReact: false,
          rotationSpeed: 0.35, rotationAudioReact: true, bloomAmount: 0.45, speedScale: 1.0, masterIntensity: 1.0,
          hueShift: 0.0, colorWheelActive: false,
        }
      },
      {
        id: "ocean_bio",
        label: "🌊 Ocean Bio (Bioluminescence)",
        state: {
          sunOn: false, fireballOn: false, blackHoleOn: false, neutronStarOn: false, cyberGridOn: false, sphereOn: false,
          bioluminescenceOn: true, chromaWaveOn: true, causticsOn: true, beatFlashOn: false, permanentColor: 0,
          dofBlur: 0.50, dofInvert: false, dofAudioReact: true, rotationSpeed: 0.25, rotationAudioReact: true,
          bloomAmount: 0.45, speedScale: 0.9, masterIntensity: 1.0, bioIntensity: 1.1, hueShift: 0.0, colorWheelActive: false,
        }
      },
      {
        id: "film_noir",
        label: "🎬 Film Noir (B&W Bokeh)",
        state: {
          sunOn: false, fireballOn: false, blackHoleOn: false, neutronStarOn: false, cyberGridOn: false, sphereOn: false,
          bioluminescenceOn: false, spectrumColorOn: false, chromaWaveOn: false, beatFlashOn: false, permanentColor: 3,
          dofBlur: 1.4, dofInvert: false, dofAudioReact: true, rotationSpeed: 0.15, rotationAudioReact: false,
          bloomAmount: 0.35, speedScale: 0.85, masterIntensity: 1.0, hueShift: 0.0, colorWheelActive: false,
        }
      },
    ];

    function applyPreset(presetData) {
      resetEffects();
      Object.assign(state, presetData);
      if (presetData.permanentColor !== undefined) setPermanentColor(presetData.permanentColor);
    }

    const presetBox = document.createElement("div");
    presetBox.className = "toolBox presetBox";

    const presetHeader = document.createElement("div");
    presetHeader.className = "toolBoxHeader";
    presetHeader.textContent = t("boxPresets");
    presetBox.appendChild(presetHeader);
    makeCollapsible(presetBox, presetHeader, "starcode.ui.presetBox");

    const presetContent = document.createElement("div");
    presetContent.className = "presetContent";

    const presetSelect = document.createElement("select");
    presetSelect.className = "formatSelect presetSelect";

    function loadUserPresets() {
      try {
        return JSON.parse(localStorage.getItem("starcode.userPresets.v1") || "[]");
      } catch {
        return [];
      }
    }

    function saveUserPresets(arr) {
      localStorage.setItem("starcode.userPresets.v1", JSON.stringify(arr));
    }

    function renderPresetOptions() {
      presetSelect.innerHTML = "";
      const groupFactory = document.createElement("optgroup");
      groupFactory.label = t("presetGroupFactory");
      for (const fp of FACTORY_PRESETS) {
        const opt = document.createElement("option");
        opt.value = "fp:" + fp.id;
        opt.textContent = fp.label;
        groupFactory.appendChild(opt);
      }
      presetSelect.appendChild(groupFactory);

      const userList = loadUserPresets();
      if (userList.length > 0) {
        const groupUser = document.createElement("optgroup");
        groupUser.label = t("presetGroupUser");
        userList.forEach((up, idx) => {
          const opt = document.createElement("option");
          opt.value = "up:" + idx;
          opt.textContent = "★ " + up.name;
          groupUser.appendChild(opt);
        });
        presetSelect.appendChild(groupUser);
      }
    }

    renderPresetOptions();

    presetSelect.addEventListener("change", () => {
      const val = presetSelect.value;
      if (val.startsWith("fp:")) {
        const id = val.slice(3);
        const fp = FACTORY_PRESETS.find((x) => x.id === id);
        if (fp) applyPreset(fp.state);
      } else if (val.startsWith("up:")) {
        const idx = parseInt(val.slice(3), 10);
        const userList = loadUserPresets();
        if (userList[idx]) applyPreset(userList[idx].state);
      }
    });

    const presetBtnRow = document.createElement("div");
    presetBtnRow.className = "presetBtnRow";

    const savePresetBtn = document.createElement("button");
    savePresetBtn.type = "button";
    savePresetBtn.className = "ctrlBtn presetSaveBtn";
    savePresetBtn.textContent = t("presetSave");
    savePresetBtn.title = t("presetSave");
    savePresetBtn.addEventListener("click", () => {
      const userList = loadUserPresets();
      const name = prompt(t("presetPromptName"), `Preset ${userList.length + 1}`);
      if (!name || !name.trim()) return;
      const currentState = {};
      for (const [key, value] of Object.entries(state)) {
        if (typeof value === "boolean" || typeof value === "number") {
          currentState[key] = value;
        }
      }
      userList.push({ name: name.trim(), state: currentState });
      saveUserPresets(userList);
      renderPresetOptions();
      presetSelect.value = "up:" + (userList.length - 1);
    });

    const deletePresetBtn = document.createElement("button");
    deletePresetBtn.type = "button";
    deletePresetBtn.className = "ctrlBtn presetDeleteBtn";
    deletePresetBtn.textContent = t("presetDelete");
    deletePresetBtn.title = t("presetDelete");
    deletePresetBtn.addEventListener("click", () => {
      const val = presetSelect.value;
      if (!val.startsWith("up:")) {
        alert(t("presetAlertFactory"));
        return;
      }
      const idx = parseInt(val.slice(3), 10);
      const userList = loadUserPresets();
      userList.splice(idx, 1);
      saveUserPresets(userList);
      renderPresetOptions();
      presetSelect.value = "fp:standard";
      const fp = FACTORY_PRESETS[0];
      if (fp) applyPreset(fp.state);
    });

    presetBtnRow.appendChild(savePresetBtn);
    presetBtnRow.appendChild(deletePresetBtn);

    presetContent.appendChild(presetSelect);
    presetContent.appendChild(presetBtnRow);
    presetBox.appendChild(presetContent);
    bottomBar.appendChild(presetBox);

    container.appendChild(bottomBar);

    // --- Dynamic Resize Handle Logic ---
    const resizeHandle = document.getElementById("hudResizeHandle");
    if (resizeHandle) {
      let dragging = false;
      let startY = 0;
      let startHeight = 0;

      const onPointerDown = (e) => {
        dragging = true;
        startY = e.clientY;
        startHeight = container.getBoundingClientRect().height;
        document.body.style.userSelect = "none";
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
      };

      const onPointerMove = (e) => {
        if (!dragging) return;
        const dy = startY - e.clientY;
        const newHeight = Math.max(80, Math.min(window.innerHeight * 0.85, startHeight + dy));
        container.style.maxHeight = `${newHeight}px`;
        container.style.height = `${newHeight}px`;
      };

      const onPointerUp = () => {
        dragging = false;
        document.body.style.userSelect = "";
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
      };

      resizeHandle.addEventListener("pointerdown", onPointerDown);

      resizeHandle.addEventListener("dblclick", () => {
        if (container.style.height === "80px") {
          container.style.height = "auto";
          container.style.maxHeight = "38vh";
        } else {
          container.style.height = "80px";
          container.style.maxHeight = "80px";
        }
      });
    }

    // --- MIDI Learn row ---
    if (midi) {
      const midiRow = document.createElement("div");
      midiRow.className = "controlSection midiSection";
      const midiLabel = document.createElement("span");
      midiLabel.className = "controlSectionLabel";
      midiLabel.textContent = t("boxMidi");
      midiRow.appendChild(midiLabel);
      makeCollapsible(midiRow, midiLabel, "starcode.ui.midiRow", true);

      for (const [key, target] of Object.entries(MIDI_TARGETS)) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "ctrlBtn";
        btn.addEventListener("click", () => {
          midi.armLearn(midi.learnTarget === key ? null : key);
        });
        midiRow.appendChild(btn);
        trackedLearnButtons.push({ el: btn, key, label: target.label });
      }
      container.appendChild(midiRow);
    }
  }

  renderBar();

  window.addEventListener("starcode:langchange", () => {
    renderBar();
  });

  // Called every frame from main.js's animate loop so buttons stay visually in sync
  return function syncControlBar() {
    for (const tracked of trackedButtons) {
      let active = false;
      if (tracked.kind === "checkbox") {
        active = !!state[tracked.field];
        if (tracked.el.checked !== active) tracked.el.checked = active;
      } else if (tracked.kind === "toggle" || tracked.kind === "held") {
        active = !!state[tracked.field];
      } else if (tracked.kind === "morph") {
        active = state.morphOn;
      } else if (tracked.kind === "objMorph") {
        active = state.objMorphOn;
      } else if (tracked.kind === "brandMorph") {
        active = state.brandMorphOn;
      } else if (tracked.kind === "permanentColor") {
        active = state.permanentColor === tracked.mode;
      }
      if (tracked.kind !== "checkbox") {
        tracked.el.classList.toggle("active", active);
      }
      tracked.isActive = active;
      if (tracked.field === "dofInvert") {
        tracked.el.textContent = active ? t("btnNearBokeh") : t("btnFarBokeh");
      }
      if (tracked.field === "dofAudioReact") {
        tracked.el.textContent = active ? t("btnAudioBokehActive") : t("btnAudioBokeh");
      }
      if (tracked.field === "autofocusOn") {
        tracked.el.textContent = active ? t("btnAFActive") : t("btnAF");
      }
    }

    // Per-category counter
    for (const cat of trackedCategories) {
      let n = 0;
      for (let i = cat.from; i < cat.to; i++) if (trackedButtons[i].isActive) n++;
      const text = n > 0 ? String(n) : "";
      if (cat.badge.textContent !== text) cat.badge.textContent = text;
      cat.badge.classList.toggle("on", n > 0);
    }

    // Sliders
    for (const s of trackedSliders) {
      if (document.activeElement === s.input) continue;
      const v = state[s.field];
      if (Math.abs(parseFloat(s.input.value) - v) > 0.001) s.input.value = v;
    }

    for (const lb of trackedLearnButtons) {
      const arming = midi?.learnTarget === lb.key;
      const cc = midi?.ccForTarget(lb.key);
      lb.el.textContent = arming
        ? `${lb.label}: ${getLang() === "en" ? "Move knob..." : "Regler bewegen…"}`
        : cc != null
          ? `${lb.label} (CC${cc})`
          : `${lb.label} —`;
      lb.el.classList.toggle("active", arming);
    }

    // Format inputs
    if (inputW && document.activeElement !== inputW && state.outputWidth && String(state.outputWidth) !== inputW.value) {
      inputW.value = state.outputWidth;
    }
    if (inputH && document.activeElement !== inputH && state.outputHeight && String(state.outputHeight) !== inputH.value) {
      inputH.value = state.outputHeight;
    }
    if (formatSelect && document.activeElement !== formatSelect && state.outputPreset && formatSelect.value !== state.outputPreset) {
      formatSelect.value = state.outputPreset;
    }

    // Color status update
    const isColorActive = state.colorWheelActive || state.hueShift > 0.001;
    if (thumb && colorStatus && colorResetBtn) {
      if (isColorActive) {
        const deg = Math.round(state.hueShift * 360);
        colorStatus.textContent = `${deg}°`;
        thumb.style.left = `${(state.hueShift * 100).toFixed(1)}%`;
        thumb.style.background = `hsl(${deg}, 95%, 55%)`;
        thumb.style.boxShadow = `0 0 10px hsl(${deg}, 95%, 55%)`;
        thumb.style.opacity = "1";
        colorResetBtn.classList.remove("hidden");
      } else {
        colorStatus.textContent = t("colorOff");
        thumb.style.left = "0%";
        thumb.style.background = "#ffffff";
        thumb.style.boxShadow = "none";
        thumb.style.opacity = "0";
        colorResetBtn.classList.add("hidden");
      }
    }
  };
}
